import { memo, useCallback, useMemo, useState } from "react";
import CakeIcon from "@mui/icons-material/Cake";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ForumIcon from "@mui/icons-material/Forum";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MedicalServices from "@mui/icons-material/MedicalServices";
import SettingsIcon from "@mui/icons-material/Settings";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";

import PostAttendenceDialog from "@/components/schedule/PostAttendenceDialog";
import { oids } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import CommunicationsBirthdayPatientsDialog from "src-next/modules/crm-clinical/communications/components/CommunicationsBirthdayPatientsDialog";
import CommunicationsFindPatientsDialog from "src-next/modules/crm-clinical/communications/components/CommunicationsFindPatientsDialog";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { useCommunicationsWhatsAppSendDialog } from "src-next/modules/crm-clinical/communications/hooks/useCommunicationsWhatsAppSendDialog";
import { useClinicalCrmCommunicationsViewModel } from "src-next/modules/crm-clinical/communications/useClinicalCrmCommunicationsViewModel";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import { useCrmSetupEligibility } from "src-next/modules/crm-clinical/setup/hooks/useCrmSetupEligibility";
import FluxZapQuickActionCard, {
  type FluxZapQuickActionCardSize,
} from "src-next/shared/ui/fluxzap/FluxZapQuickActionCard";

import type { ClinicalCrmSectionId } from "../types";

const FLUXZAP_PANEL_PATH = "/fluxzap";

export type FluxZapQuickActionsContext = "schedule" | "crm";

export interface FluxZapQuickActionsPanelProps {
  /** `compact` no CRM; `default` na agenda. */
  cardSize?: FluxZapQuickActionCardSize;
  context: FluxZapQuickActionsContext;
  organizationId?: string;
  practitionerId?: string;
  onMessageSent?: () => void;
  /** CRM: rolar e expandir seção ao clicar no atalho. */
  onFocusSection?: (sectionId: ClinicalCrmSectionId) => void;
  statsRefreshSignal?: number;
  /** Ecommerce: oculta o atalho de pós-atendimento (só UI). */
  hidePostAttendanceQuickAction?: boolean;
}

/**
 * Painel de ações rápidas FluxZap (diálogos + atalhos de seção ou navegação).
 */
const FluxZapQuickActionsPanel = memo(
  ({
    cardSize = "default",
    context,
    organizationId: organizationIdProp,
    practitionerId: practitionerIdProp,
    onMessageSent,
    onFocusSection,
    statsRefreshSignal = 0,
    hidePostAttendanceQuickAction = false,
  }: FluxZapQuickActionsPanelProps) => {
    const navigate = useNavigate();
    const [findOpen, setFindOpen] = useState(false);
    const [birthdayOpen, setBirthdayOpen] = useState(false);
    const [postAttendanceOpen, setPostAttendanceOpen] = useState(false);

    const { access, user } = useSessionContext();
    const { organizationId: contextOrganizationId } = useOrganizationContext();

    const effectiveOrganizationId = useMemo(
      () =>
        resolveCrmOrganizationId(
          organizationIdProp,
          contextOrganizationId,
          access?.organizationId,
        ),
      [organizationIdProp, contextOrganizationId, access?.organizationId],
    );

    const practitionerId = useMemo(
      () =>
        practitionerIdProp ??
        (user?.username ? `${oids.cpf}-${user.username}` : undefined),
      [practitionerIdProp, user?.username],
    );

    const eligibility = useCrmSetupEligibility({
      organizationId: effectiveOrganizationId,
    });

    const isFluxZapConfigured =
      !eligibility.isLoading &&
      (eligibility.hasActiveMetaEndpoint || eligibility.hasCrmContract);

    const handleSetupRedirect = useCallback(() => {
      navigate(FLUXZAP_PANEL_PATH);
    }, [navigate]);

    const vm = useClinicalCrmCommunicationsViewModel({
      organizationId: effectiveOrganizationId,
      refreshSignal: statsRefreshSignal,
    });

    const { openSendMessage, sendDialogProps } =
      useCommunicationsWhatsAppSendDialog({
        organizationId: effectiveOrganizationId,
        practitionerId,
        onMessageSent,
        franchiseRefreshSignal: statsRefreshSignal,
      });

    const communicationsReady = vm.hasOrganization && !vm.isLoading;

    const handlePostAttendance = () => {
      setPostAttendanceOpen(true);
    };

    const fourthAction =
      context === "schedule"
        ? {
            title: "Acessar painel",
            description: "Painel completo com templates e configurações",
            icon: (
              <DashboardIcon
                sx={{ fontSize: cardSize === "compact" ? 22 : 32 }}
              />
            ),
            avatarBgColor: "success.main",
            onClick: () => navigate(FLUXZAP_PANEL_PATH),
            disabled: false,
            ariaLabel: "Acessar painel FluxZap",
          }
        : {
            title: "Comunicações",
            description:
              "Histórico de disparos e análise por modelo ou paciente",
            icon: (
              <ForumIcon sx={{ fontSize: cardSize === "compact" ? 22 : 32 }} />
            ),
            avatarBgColor: "success.main",
            onClick: () => onFocusSection?.("communications"),
            disabled: false,
            ariaLabel: "Ir para gestão de comunicações",
          };

    const isCompact = cardSize === "compact";
    const desktopColumns = hidePostAttendanceQuickAction ? 3 : 4;

    return (
      <>
        <Paper
          elevation={0}
          sx={{
            p: isCompact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: isCompact
              ? "0px 2px 10px rgba(0, 0, 0, 0.06)"
              : "0px 4px 14px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Stack spacing={isCompact ? 1.5 : 2}>
            <Box>
              {context === "crm" && (
                <Typography
                  variant={isCompact ? "subtitle1" : "h6"}
                  fontWeight={700}
                >
                  Acesso rápido
                </Typography>
              )}

              {!isFluxZapConfigured && !eligibility.isLoading ? (
                <Typography
                  variant="caption"
                  color="warning.main"
                  fontWeight={600}
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Configure o FluxZap para usar estas funcionalidades.
                </Typography>
              ) : !communicationsReady ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {vm.isLoading
                    ? "Carregando pacientes e comunicações..."
                    : "Listas exigem organização ativa na sessão."}
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  md: isCompact
                    ? `repeat(${desktopColumns}, 1fr)`
                    : "repeat(2, 1fr)",
                },
                gap: isCompact ? 1.25 : 2,
                width: "100%",
              }}
            >
              <FluxZapQuickActionCard
                size={cardSize}
                title="Encontrar pacientes"
                description="Filtre e envie mensagens pelo WhatsApp"
                icon={
                  <ManageSearchIcon sx={{ fontSize: isCompact ? 22 : 32 }} />
                }
                avatarBgColor="primary.main"
                onClick={
                  isFluxZapConfigured
                    ? () => setFindOpen(true)
                    : handleSetupRedirect
                }
                disabled={isFluxZapConfigured && !communicationsReady}
                ariaLabel="Encontrar ou filtrar pacientes"
              />

              <FluxZapQuickActionCard
                size={cardSize}
                title="Aniversariantes"
                description="Parabéns e mensagens para aniversariantes"
                icon={<CakeIcon sx={{ fontSize: isCompact ? 22 : 32 }} />}
                avatarBgColor="secondary.main"
                onClick={
                  isFluxZapConfigured
                    ? () => setBirthdayOpen(true)
                    : handleSetupRedirect
                }
                disabled={isFluxZapConfigured && !communicationsReady}
                ariaLabel="Aniversariantes"
              />

              {!hidePostAttendanceQuickAction ? (
                <FluxZapQuickActionCard
                  size={cardSize}
                  title="Pós-atendimento"
                  description="Medicamentos, exames e acompanhamento"
                  icon={
                    <MedicalServices sx={{ fontSize: isCompact ? 22 : 32 }} />
                  }
                  avatarBgColor="info.main"
                  onClick={
                    isFluxZapConfigured
                      ? handlePostAttendance
                      : handleSetupRedirect
                  }
                  ariaLabel="FluxZap pós-atendimento"
                />
              ) : null}

              {isFluxZapConfigured || eligibility.isLoading ? (
                <FluxZapQuickActionCard
                  size={cardSize}
                  title={fourthAction.title}
                  description={fourthAction.description}
                  icon={fourthAction.icon}
                  avatarBgColor={fourthAction.avatarBgColor}
                  onClick={fourthAction.onClick}
                  disabled={fourthAction.disabled}
                  ariaLabel={fourthAction.ariaLabel}
                />
              ) : (
                <FluxZapQuickActionCard
                  size={cardSize}
                  title="Configurar FluxZap"
                  description="Vincule WhatsApp e escolha seu plano de mensagens"
                  icon={
                    <SettingsIcon sx={{ fontSize: isCompact ? 22 : 32 }} />
                  }
                  avatarBgColor="warning.main"
                  onClick={handleSetupRedirect}
                  ariaLabel="Ir para configuração do FluxZap"
                />
              )}
            </Box>
          </Stack>
        </Paper>

        {findOpen && (
          <CommunicationsFindPatientsDialog
            open={findOpen}
            onClose={() => setFindOpen(false)}
            allPatients={vm.patients}
            communications={vm.communications}
            onOpenSendMessage={openSendMessage}
          />
        )}

        {birthdayOpen && (
          <CommunicationsBirthdayPatientsDialog
            open={birthdayOpen}
            onClose={() => setBirthdayOpen(false)}
            allPatients={vm.patients}
            communications={vm.communications}
            onOpenSendMessage={openSendMessage}
          />
        )}

        {!hidePostAttendanceQuickAction && postAttendanceOpen && (
          <PostAttendenceDialog
            open={postAttendanceOpen}
            onClose={() => setPostAttendanceOpen(false)}
          />
        )}

        {sendDialogProps?.open && (
          <SendWhatsAppMessageDialog {...sendDialogProps} />
        )}
      </>
    );
  },
);

FluxZapQuickActionsPanel.displayName = "FluxZapQuickActionsPanel";

export default FluxZapQuickActionsPanel;
